/**
 * Choosing the vertical window a trend chart draws in.
 *
 * Every one of these metrics is a percentage, and drawing all of them against a fixed 0–100 axis
 * flattens the thing the reader came for: a brand whose sentiment moves between 61% and 66% over a
 * quarter renders as a straight line, and a two-point drop that matters is invisible. Zooming the
 * axis to the data restores that movement.
 *
 * The risk of zooming is the opposite distortion — a one-point wobble blown up to fill the panel
 * reads as a crisis. The padding and minimum-span rules below are what keep that in check.
 */

/** The vertical window, as percentages. */
export interface ChartYDomain {
  min: number;
  max: number;
}

/** The full axis, used when there is nothing to zoom to. */
const FULL_DOMAIN: ChartYDomain = { min: 0, max: 100 };

/**
 * The narrowest window we will draw.
 *
 * Below this a flat series turns into a hairline whose every pixel of noise reads as a trend. Ten
 * points is wide enough that a genuinely flat line still looks flat.
 */
const MINIMUM_SPAN = 10;

/** Breathing room above and below the data, so points do not sit on the axis. */
const PADDING_RATIO = 0.08;
const MINIMUM_PADDING = 2;

/**
 * The vertical window for a set of plotted values.
 *
 * `thresholdPercents` are band boundaries (empty for metrics without bands). The window is widened
 * to the boundaries that bracket the data, so a reader always sees which band the line sits in and
 * how far it is from leaving it — a chart zoomed so tightly that no boundary is visible would show
 * movement while hiding what the movement means.
 */
export function chartYDomain(values: number[], thresholdPercents: number[] = []): ChartYDomain {
  const measured = values.filter((value) => Number.isFinite(value));
  if (!measured.length) {
    return FULL_DOMAIN;
  }

  let min = Math.min(...measured);
  let max = Math.max(...measured);

  // Widen to the nearest boundary on each side, where one exists.
  const below = thresholdPercents.filter((threshold) => threshold <= min);
  const above = thresholdPercents.filter((threshold) => threshold >= max);
  if (below.length) {
    min = Math.min(min, Math.max(...below));
  }
  if (above.length) {
    max = Math.max(max, Math.min(...above));
  }

  const padding = Math.max(MINIMUM_PADDING, (max - min) * PADDING_RATIO);
  min -= padding;
  max += padding;

  // Widen a too-narrow window around its own centre, so the data stays where the reader expects it.
  const shortfall = MINIMUM_SPAN - (max - min);
  if (shortfall > 0) {
    min -= shortfall / 2;
    max += shortfall / 2;
  }

  // Percentages cannot leave 0–100. Clamping one edge pushes the other out, so the window keeps its
  // width rather than collapsing against the boundary.
  if (min < 0) {
    max = Math.min(100, max - min);
    min = 0;
  }
  if (max > 100) {
    min = Math.max(0, min - (max - 100));
    max = 100;
  }

  return { min: Math.floor(min), max: Math.ceil(max) };
}

/**
 * The ticks a banded axis draws, and which of them are band boundaries.
 *
 * On a banded axis the boundaries ARE the landmarks: knowing the line sits above the Good cut-off
 * tells the reader more than knowing it sits at 63%. So every threshold inside the window gets a
 * tick, and at those positions the icon replaces the percentage rather than sitting beside it —
 * two labels on one tick is the crowding this exists to avoid.
 *
 * Plain numeric ticks fill the gaps, but only where they do not crowd a boundary. A "58%" pressed
 * against the Good icon makes both harder to read than either alone.
 */
export interface BandedAxisTicks {
  /** Every tick position, ascending. */
  values: number[];
  /** The subset that are band boundaries — these render an icon instead of a number. */
  thresholds: number[];
}

/** How close a numeric tick may come to a boundary before it is dropped, as a share of the window. */
const TICK_CROWDING_RATIO = 0.12;

export function bandedAxisTicks(min: number, max: number, thresholdPercents: readonly number[], numericTickCount = 5): BandedAxisTicks {
  const span = max - min;
  if (span <= 0) {
    return { values: [min], thresholds: [] };
  }

  const thresholds = thresholdPercents.filter((value) => value >= min && value <= max).sort((left, right) => left - right);

  // A BANDED AXIS IS ENTIRELY ICONS. The boundaries are the only landmarks that mean anything for
  // a banded score, so they are the only ticks: mixing them with numeric ticks puts two kinds of
  // label on one axis and leaves the reader deciding which to read. Numeric filler ticks exist
  // only on axes that have no bands at all.
  if (thresholds.length) {
    return { values: thresholds, thresholds };
  }

  const minimumGap = span * TICK_CROWDING_RATIO;

  const numeric: number[] = [];
  for (let index = 0; index <= numericTickCount; index += 1) {
    const candidate = Math.round(min + (span * index) / numericTickCount);
    if (candidate < min || candidate > max) {
      continue;
    }
    // Dropped when it would crowd a boundary, or duplicate a tick already placed.
    if (thresholds.some((threshold) => Math.abs(threshold - candidate) < minimumGap)) {
      continue;
    }
    if (numeric.some((placed) => Math.abs(placed - candidate) < minimumGap)) {
      continue;
    }
    numeric.push(candidate);
  }

  return {
    values: [...thresholds, ...numeric].sort((left, right) => left - right),
    thresholds,
  };
}

/**
 * The text an axis prints at one tick.
 *
 * ICONS AND NUMBERS ARE EXCLUSIVE PER AXIS, not per tick. A banded axis shows icons and NOTHING
 * else — every one of its ticks is a boundary, and each is labelled by its glyph. An unbanded axis
 * shows numbers and has no icons at all.
 *
 * There is deliberately no branch that returns a number while banded: that path is what produced a
 * sentiment axis carrying icons at the boundaries and percentages in between, which is the mix this
 * rule exists to forbid.
 */
export function bandedAxisTickLabel(value: number, banded: boolean): string {
  return banded ? '' : `${value}%`;
}
